import {Component, ViewChild} from '@angular/core';
import {FormBuilder, FormControlStatus, FormGroup, ɵFormGroupRawValue, ɵTypedOrUntyped} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {
    TrendApiService,
    TrendIncidentResponse,
    TrendResponse,
    TrendStatisticResponse,
    UpdateTrendRequest,
    UpdateTrendStatisticRequest,
} from '@npck/af-frontend-api';
import {DATE_SERVER_FORMAT, NESTED_TAB, RouterService, rxSpinner, Spinner, TAB} from '@npck/libs/commons';
import {DialogService, SnackbarService, StepperComponent} from '@npck/libs/ui-kit';
import {isEqual} from 'lodash-es';
import {combineLatest, debounceTime, distinctUntilChanged, filter, Observable, of, Subscription} from 'rxjs';
import {getFormattedDateOrNull} from '../../../../components/full-table/filter/filter.component';
import {TREND_ID} from '../../../../domain/query-params.keys';
import {Path} from '../../../../path';
import {isAnyFormFillStarted} from '../../../../utils/form.utils';
import {ANALYTIC_TREND_TAB, AnalyticsTab} from '../../analytics.component';
import {TrendDetailsTab} from '../details/trend-details.component';
import {TrendIncidentsForm} from './trend-incidents-step/trend-incidents-step.component';
import {TrendInformationForm} from './trend-information-step/trend-information-step.component';
import {TrendStatisticForm} from './trend-statistics-step/trend-statistic/trend-statistic.component';
import {TrendStatisticsForm} from './trend-statistics-step/trend-statistics-step.component';

@Component({
    templateUrl: './create-edit-trend.component.html',
    standalone: false,
})
export class CreateEditTrendComponent {
    @ViewChild('stepper') stepper: StepperComponent;
    Spinner = Spinner;
    form: FormGroup<TrendForm>;
    steps: Step[] = ['trend', 'incidents', 'statistics'];
    currentStep: Step = 'trend';
    lastSteps: Step[] = ['statistics'];
    isButtonDisabled: boolean = true;
    hasReachedLastStep: boolean = false;
    trendId: string;
    trend: TrendResponse;
    trendIncidents: TrendIncidentResponse[];
    trendStatistics: TrendStatisticResponse[];
    private initialFormValue: TrendFormValue;
    private formInitializeSubscription: Subscription;
    private backToNestedTab: TrendDetailsTab;
    private isFormChanged: boolean;

    constructor(
        private activatedRoute: ActivatedRoute,
        private routerService: RouterService,
        private formBuilder: FormBuilder,
        private trendApiService: TrendApiService,
        private snackbarService: SnackbarService,
        private dialogService: DialogService,
    ) {
        this.trendId = this.activatedRoute.snapshot.paramMap.get(TREND_ID);
        this.backToNestedTab = this.activatedRoute.snapshot.queryParamMap.get(NESTED_TAB) as TrendDetailsTab;

        if (this.trendId) {
            combineLatest([
                this.trendApiService.getTrend(this.trendId),
                this.trendApiService.getTrendIncidents(this.trendId),
                this.trendApiService.getTrendStatistics(this.trendId, {sortOrder: 'DESC'}),
            ])
                .pipe(rxSpinner('initial'))
                .subscribe(([trend, trendIncidents, trendStatistics]) => {
                    this.buildForm(trend, trendIncidents, trendStatistics);
                    this.subscribeToFormInitializations();
                });
        }
    }

    get currentStepFormValid(): boolean {
        switch (this.currentStep) {
            case 'trend':
                return this.form.controls.trendInformationForm?.valid ?? true;
            case 'incidents':
                return this.form.controls.trendIncidentsForm?.valid ?? true;
            case 'statistics':
                return this.form.controls.trendStatisticsForm?.valid ?? true;
        }
    }

    isStepCompleted = (stepName: string): boolean => {
        switch (stepName as Step) {
            case 'trend':
                return this.form.controls.trendInformationForm?.valid ?? false;
            case 'incidents':
                return this.form.controls.trendIncidentsForm?.valid ?? false;
            case 'statistics':
                return this.form.controls.trendStatisticsForm?.valid ?? false;
        }
    };

    filterTrendIdPath = (path: string): boolean => {
        return path === this.trendId;
    };

    navigateBack = (): Observable<boolean> => {
        if ((!this.trendId && isAnyFormFillStarted(this.form)) || (this.trendId && this.isFormChanged)) {
            return this.dialogService.openDialog({
                title: 'i18n_af_dialogCreateIncidentWarningBeforeExitTitle',
                subtitle: 'i18n_af_dialogEditTrendWarningBeforeExitSubtitle',
                buttonOkText: 'i18n_af_buttonExit',
                buttonCancelText: 'i18n_af_buttonCancel',
                isButtonOkAccent: true,
            }, {disableClose: true});
        } else {
            return of(true);
        }
    };

    openCancellationWarningDialog(): void {
        this.navigateBack().subscribe(result => {
            if (result) {
                if (this.trendId) {
                    this.routerService.navigate(Path.ANALYTICS_TREND_DETAILS, {
                        pathParams: {
                            [TREND_ID]: this.trendId,
                        },
                        queryParams: {
                            [TAB]: ANALYTIC_TREND_TAB,
                            [NESTED_TAB]: this.backToNestedTab,
                        },
                    });
                } else {
                    this.routerService.navigateToTab<AnalyticsTab>(Path.ANALYTICS, ANALYTIC_TREND_TAB);
                }
            }
        });
    }

    onStepChange(index: number): void {
        this.currentStep = this.steps[index];
        if (index === this.steps.length - 1) {
            this.hasReachedLastStep = true;
        }
    }

    sendRequest(): void {
        this.form.markAllAsTouched();
        if (this.form.invalid) {
            return;
        }
        if (this.trendId && !this.isFormChanged) {
            return;
        }
        this.updateTrend();
    }

    changeStepper(event: string): void {
        if (!event) {
            return;
        }
        this.hasReachedLastStep = false;
        if (!this.form.controls.trendInformationForm) {
            this.form.addControl('trendInformationForm', this.formBuilder.group({}));
        }
        if (!this.form.controls.trendIncidentsForm) {
            this.form.addControl('trendIncidentsForm', this.formBuilder.group({}));
        }
        if (!this.form.controls.trendStatisticsForm) {
            this.form.addControl('trendStatisticsForm', this.formBuilder.group({}));
        }
    }

    private subscribeToFormInitializations(): void {
        this.formInitializeSubscription = this.form.statusChanges.pipe(
            rxSpinner('initForms'),
            debounceTime(500),
            distinctUntilChanged(),
            filter((status: FormControlStatus) => status !== 'PENDING'),
        ).subscribe(() => {
            this.formInitializeSubscription.unsubscribe();
            this.initialFormValue = this.form.getRawValue();
            this.subscribeToFormChanges();
        });
    }

    private updateTrend(): void {
        this.trendApiService.updateTrend(this.trendId, this.createTrendUpdateRequest())
            .pipe(rxSpinner('editTrend'))
            .subscribe(() => {
                this.snackbarService.showSnackBar('i18n_af_snackBarMessageIncidentUpdated');
                this.routerService.navigate(Path.ANALYTICS_TREND_DETAILS, {
                    pathParams: {
                        [TREND_ID]: this.trendId,
                    },
                    queryParams: {
                        [TAB]: ANALYTIC_TREND_TAB,
                        [NESTED_TAB]: this.backToNestedTab,
                    }
                });
            });
    }

    private createTrendUpdateRequest(): UpdateTrendRequest {
        const trendInfoFormValue = this.form.getRawValue().trendInformationForm;
        const trendIncidentsFormValue = this.form.getRawValue().trendIncidentsForm?.deletedTrendIncidents;

        const trendStatisticFormArray = [
            ...(this.form.controls?.trendStatisticsForm.controls.trendStatistics.controls ?? []),
            ...(this.form.controls?.trendStatisticsForm.controls.deletedTrendStatistics.controls ?? []),
        ];

        return <UpdateTrendRequest> {
            description: trendInfoFormValue?.description,
            keywords: trendInfoFormValue?.keywords,
            conclusion: trendInfoFormValue?.conclusion,
            deletedIncidentIds: trendIncidentsFormValue?.map(it => it.incidentId) ?? [],
            statistics: trendStatisticFormArray?.map(it => this.createUpdateTrendStatisticRequest(it)) ?? [],
        };
    }

    private createUpdateTrendStatisticRequest(statisticForm: FormGroup<TrendStatisticForm>): UpdateTrendStatisticRequest {
        const rawFormValue = statisticForm.getRawValue();

        return <UpdateTrendStatisticRequest> {
            id: rawFormValue.id,
            country: rawFormValue.country,
            region: rawFormValue.region,
            uniqueVictimCount: Number(rawFormValue.uniqueVictimCount),
            periodDetectedFrom: getFormattedDateOrNull(rawFormValue.periodRange.dateFrom, DATE_SERVER_FORMAT),
            periodDetectedTo: getFormattedDateOrNull(rawFormValue.periodRange.dateTo, DATE_SERVER_FORMAT),
            isDeleted: rawFormValue.isDeleted,
        };
    }

    private buildForm(
        trend: TrendResponse,
        trendIncidents: TrendIncidentResponse[],
        trendStatistics: TrendStatisticResponse[]
    ): void {
        this.trend = trend;
        this.trendIncidents = trendIncidents;
        this.trendStatistics = trendStatistics;

        this.form = this.formBuilder.group({});
        this.form.addControl('trendInformationForm', this.formBuilder.group({}));
        this.form.addControl('trendIncidentsForm', this.formBuilder.group({}));
        this.form.addControl('trendStatisticsForm', this.formBuilder.group({}));

        Spinner.hide('initial');
    }

    private subscribeToFormChanges(): void {
        this.form.valueChanges.pipe(
            debounceTime(500),
            distinctUntilChanged(),
        ).subscribe(value => {
            this.isFormChanged = !isEqual(this.initialFormValue, value);
            this.isButtonDisabled = !this.isFormChanged;
        });
    }

}

declare type TrendFormValue = ɵTypedOrUntyped<TrendForm, ɵFormGroupRawValue<TrendForm>, any>;

export interface TrendForm {
    trendInformationForm?: FormGroup<TrendInformationForm>;
    trendIncidentsForm?: FormGroup<TrendIncidentsForm>;
    trendStatisticsForm?: FormGroup<TrendStatisticsForm>;
}

declare type Step = 'trend' | 'incidents' | 'statistics';
